/**
 * ルール誤解の検知（TK-041、FR-025）。
 *
 * 第 1 段階（秘文字）の課題は τ を揃えることだが、初見のプレイヤーは絵柄を先に
 * 組もうとしがちである。**画像基準の仮想グループ**（盤面上で連続していて、相対位置が
 * 完成画像の相対位置と一致するまとまり。絶対位置＝オフセットは問わない）が閾値以上に
 * 育ったら、ルールの取り違えとみなして一度だけヒントを出す。
 *
 * 「仮想」なのは、第 1 段階の結合判定（`computeGroups(placement, τ, C)`）とは別に、
 * 完成画像の目標（identity）でグループを数え直すためである。第 1 段階では画像側の
 * まとまりは結合もハイライトもされない — だからこそプレイヤーには進捗に見えてしまう。
 */

import { computeGroups } from '../core/groups';
import { IDENTITY_TARGET } from '../core/stage';
import type { Placement } from '../core/types';

/** ヒントを出す仮想グループの下限ピース数。 */
export const RULE_HINT_CLUSTER = 10;

/** ヒントの文言。 */
export const RULE_HINT_TEXT = '先にお邪魔文字を揃えないと、文字が消えません！';

/**
 * 画像基準の仮想グループの最大サイズ。
 *
 * `computeGroups` の結合条件は「隣接ペアの相対位置が目標の相対位置と一致する」ことなので、
 * 目標に identity を渡せば、盤面のどこに寄せてあっても（オフセットが違っても）
 * 「絵として連続しているまとまり」がそのまま連結成分になる。
 */
export function largestImageCluster(placement: Placement, C: number): number {
  const groups = computeGroups(placement, IDENTITY_TARGET(placement.length), C);
  let largest = 0;
  for (const group of groups.members) {
    if (group.length > largest) largest = group.length;
  }
  return largest;
}

/**
 * ヒント表示のハンドル。1 ステージにつき 1 回だけ表示する（毎ドロップで再警告しない）。
 */
export interface RuleHint {
  /** ドロップ確定ごとに呼ぶ。条件を満たした最初の 1 回だけ表示する。 */
  readonly check: (placement: Placement, phase: 1 | 2) => void;
}

/**
 * ヒント要素を `stage` に取り付け、判定ハンドルを返す。
 *
 * 表示は **OK で明示的に閉じるモーダル**（`.rule-hint`、`main.css`）。ルールを思い込んで
 * いるプレイヤーは一瞬のトーストに気づかないため、自動では消さない。閉じるまで盤面への
 * 入力はオーバーレイが遮る。`window.alert` は使わない — ブラウザのモーダルダイアログは
 * イベントループごと止め、ドラッグ中の状態や自動テストを壊すため（DOM 内のモーダルで足りる）。
 */
export function attachRuleHint(stage: HTMLElement, C: number): RuleHint {
  const doc = stage.ownerDocument;
  const overlay = doc.createElement('div');
  overlay.className = 'rule-hint';
  overlay.hidden = true;
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'ルールのヒント');

  const panel = doc.createElement('div');
  panel.className = 'rule-hint-panel';
  const text = doc.createElement('p');
  text.className = 'rule-hint-text';
  text.textContent = RULE_HINT_TEXT;
  const ok = doc.createElement('button');
  ok.type = 'button';
  ok.className = 'rule-hint-ok';
  ok.textContent = 'OK';
  ok.addEventListener('click', () => {
    overlay.hidden = true;
  });
  panel.append(text, ok);
  overlay.append(panel);
  stage.append(overlay);

  let shown = false;
  return {
    check: (placement, phase) => {
      if (shown || phase !== 1) return;
      if (largestImageCluster(placement, C) < RULE_HINT_CLUSTER) return;
      shown = true;
      overlay.hidden = false;
      ok.focus();
    },
  };
}
